import { useState, useEffect, useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ragApi, RagApiError } from '../services/ApiService'
import type { 
  ChatbotSettings, 
  ApiChatbotSettings
} from '../lib/types'
import { DataTransformers } from '../lib/types'
import { queryKeys } from '@/queries/keys'

interface UseSettingsResult {
  settings: ChatbotSettings | null
  loading: boolean
  error: string | null
  updateSettings: (data: Partial<ChatbotSettings>) => Promise<void>
  refetch: () => Promise<void>
}

/**
 * Hook for managing chatbot settings
 */
export function useSettings(): UseSettingsResult {
  const queryClient = useQueryClient()
  const [mutationError, setMutationError] = useState<string | null>(null)

  const settingsQuery = useQuery({
    queryKey: queryKeys.knowledge.settings(),
    queryFn: async () => {
      const apiSettings = await ragApi.getChatbotSettings() as ApiChatbotSettings
      return DataTransformers.apiSettingsToSettings(apiSettings)
    },
    staleTime: 120_000,
    refetchOnMount: false,
    retry: 0,
  })

  const updateSettingsMutation = useMutation({
    mutationFn: async (data: Partial<ChatbotSettings>) => {
      const currentSettings = settingsQuery.data || {
        text: '',
        isActive: true,
        askEmail: false,
        askPhone: false,
        askName: false,
      }

      const updatedSettings = { ...currentSettings, ...data }
      const apiData = DataTransformers.settingsToApiSettings(updatedSettings)
      await ragApi.updateChatbotSettings(apiData)
      return updatedSettings
    },
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(queryKeys.knowledge.settings(), updatedSettings)
    },
  })

  const updateSettings = useCallback(async (data: Partial<ChatbotSettings>) => {
    try {
      setMutationError(null)
      await updateSettingsMutation.mutateAsync(data)
    } catch (err) {
      console.error('Error updating settings:', err)
      const errorMessage = err instanceof RagApiError ? err.message : 'Failed to update settings'
      setMutationError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [updateSettingsMutation])

  return {
    settings: settingsQuery.data || null,
    loading: settingsQuery.isLoading,
    error: mutationError || (settingsQuery.error instanceof Error ? settingsQuery.error.message : null),
    updateSettings,
    refetch: async () => {
      await settingsQuery.refetch()
    }
  }
}

/**
 * Hook for AI model settings (local state management)
 */
export function useAISettings() {
  const [aiApiKey, setAiApiKey] = useState('')
  const [aiModel, setAiModel] = useState('gpt-4o')
  const [aiChannels, setAiChannels] = useState({
    whatsapp: true,
    instagram: true,
    messenger: false,
    web: true,
  })
  const [aiEnabled, setAiEnabled] = useState(true)

  // Load from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('ai-settings')
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings)
        setAiApiKey(parsed.apiKey || '')
        setAiModel(parsed.model || 'gpt-4o')
        setAiChannels(parsed.channels || {
          whatsapp: true,
          instagram: true,
          messenger: false,
          web: true,
        })
        setAiEnabled(parsed.enabled ?? true)
      } catch (error) {
        console.error('Error loading AI settings:', error)
      }
    }
  }, [])

  // Save to localStorage whenever settings change
  const saveToLocalStorage = useCallback(() => {
    const settingsToSave = {
      apiKey: aiApiKey,
      model: aiModel,
      channels: aiChannels,
      enabled: aiEnabled
    }
    localStorage.setItem('ai-settings', JSON.stringify(settingsToSave))
  }, [aiApiKey, aiModel, aiChannels, aiEnabled])

  useEffect(() => {
    saveToLocalStorage()
  }, [saveToLocalStorage])

  const updateAiApiKey = useCallback((key: string) => {
    setAiApiKey(key)
  }, [])

  const updateAiModel = useCallback((model: string) => {
    setAiModel(model)
  }, [])

  const updateAiChannels = useCallback((channels: Partial<typeof aiChannels>) => {
    setAiChannels(prev => ({ ...prev, ...channels }))
  }, [])

  const toggleAiChannel = useCallback((channelKey: keyof typeof aiChannels) => {
    setAiChannels(prev => ({
      ...prev,
      [channelKey]: !prev[channelKey]
    }))
  }, [])

  const updateAiEnabled = useCallback((enabled: boolean) => {
    setAiEnabled(enabled)
  }, [])

  return {
    aiApiKey,
    aiModel,
    aiChannels,
    aiEnabled,
    updateAiApiKey,
    updateAiModel,
    updateAiChannels,
    toggleAiChannel,
    updateAiEnabled
  }
}

/**
 * Hook for locale settings (local state management)
 */
export function useLocaleSettings() {
  const [selectedTimezone, setSelectedTimezone] = useState('Europe/Madrid')
  const [selectedLanguage, setSelectedLanguage] = useState('es')

  // Load from localStorage on mount
  useEffect(() => {
    const savedTimezone = localStorage.getItem('selected-timezone')
    const savedLanguage = localStorage.getItem('selected-language')
    
    if (savedTimezone) setSelectedTimezone(savedTimezone)
    if (savedLanguage) setSelectedLanguage(savedLanguage)
  }, [])

  // Save to localStorage whenever settings change
  useEffect(() => {
    localStorage.setItem('selected-timezone', selectedTimezone)
  }, [selectedTimezone])

  useEffect(() => {
    localStorage.setItem('selected-language', selectedLanguage)
  }, [selectedLanguage])

  const updateTimezone = useCallback((timezone: string) => {
    setSelectedTimezone(timezone)
  }, [])

  const updateLanguage = useCallback((language: string) => {
    setSelectedLanguage(language)
  }, [])

  const getCurrentTime = useCallback((timezone?: string) => {
    const tz = timezone || selectedTimezone
    return new Date().toLocaleTimeString('es-ES', { 
      timeZone: tz,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    })
  }, [selectedTimezone])

  return {
    selectedTimezone,
    selectedLanguage,
    updateTimezone,
    updateLanguage,
    getCurrentTime
  }
}

/**
 * Combined hook for all settings
 */
export function useAllSettings() {
  const chatbotSettings = useSettings()
  const aiSettings = useAISettings()
  const localeSettings = useLocaleSettings()

  return {
    // Chatbot settings
    chatbotSettings: chatbotSettings.settings,
    chatbotLoading: chatbotSettings.loading,
    chatbotError: chatbotSettings.error,
    updateChatbotSettings: chatbotSettings.updateSettings,
    refetchChatbotSettings: chatbotSettings.refetch,

    // AI settings
    ...aiSettings,

    // Locale settings
    ...localeSettings
  }
}

// Export types for use in components
export type { ChatbotSettings, ApiChatbotSettings }
